import { useEffect, useLayoutEffect, useRef } from 'react';

/*
  useHotkey — простой helper для global keyboard shortcuts.
  Поддерживает modifier-комбинации (cmd / ctrl) + одиночные клавиши.
  Берёт meta-key на Mac, ctrl на остальных (как в браузерах).

  Использование:
    useHotkey('mod+k', () => openSearch());
    useHotkey('Escape', () => close());
*/

type HotkeyHandler = (e: KeyboardEvent) => void;

export function useHotkey(combo: string, handler: HotkeyHandler, _deps: unknown[] = []) {
  // Подписка зависит от сочетания, обработчик всегда берётся из последнего commit.
  // Третий аргумент оставлен для совместимости существующих вызовов.
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
    const parts = combo
      .toLowerCase()
      .split('+')
      .map((p) => p.trim());
    const wantMod = parts.includes('mod') || parts.includes('cmd') || parts.includes('ctrl');
    const wantShift = parts.includes('shift');
    const wantAlt = parts.includes('alt');
    const targetKey = parts.filter((p) => !['mod', 'cmd', 'ctrl', 'shift', 'alt'].includes(p))[0];

    const onKey = (e: KeyboardEvent) => {
      if (!targetKey) return;
      const modPressed = isMac ? e.metaKey : e.ctrlKey;
      if (wantMod !== modPressed) return;
      if (wantShift !== e.shiftKey) return;
      if (wantAlt !== e.altKey) return;
      if (e.key.toLowerCase() !== targetKey) return;
      handlerRef.current(e);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo]);
}
